// Task-domain HTTP service (Layer 2 — transport). Wraps lib/http.ts's
// request() for the Task endpoints used by this feature's queries/mutations
// and the TaskPropertiesPanel/TaskDetail containers. Methods moved verbatim
// off ApiClient (apps/frontend/src/utils/api.ts), which now delegates here
// to preserve every existing `apiClient.*` call site.
import { request } from '@/lib/http'
import type { Task, TaskDependencies, TaskTemplate, RecurringTask, ActivityItem, TaskSubscriber } from '@/types'

export const tasksApi = {
  list(filters?: {
    assignee?: 'me'
    created_by?: 'me'
    status?: string
    approval?: string
    project_id?: number
    q?: string
    priority?: string
    due_before?: string
    due_after?: string
    label?: string
    flagged?: boolean
    phase_id?: string
  }): Promise<{ items: Task[]; total: number }> {
    const qs = new URLSearchParams()
    if (filters?.assignee) qs.set('assignee', filters.assignee)
    if (filters?.created_by) qs.set('created_by', filters.created_by)
    if (filters?.status) qs.set('status', filters.status)
    if (filters?.approval) qs.set('approval', filters.approval)
    if (filters?.project_id) qs.set('project_id', String(filters.project_id))
    if (filters?.q) qs.set('q', filters.q)
    if (filters?.priority) qs.set('priority', filters.priority)
    if (filters?.due_before) qs.set('due_before', filters.due_before)
    if (filters?.due_after) qs.set('due_after', filters.due_after)
    if (filters?.label) qs.set('label', filters.label)
    if (filters?.flagged) qs.set('flagged', 'true')
    if (filters?.phase_id) qs.set('phase_id', filters.phase_id)
    const q = qs.toString()
    return request(`/work/tasks${q ? `?${q}` : ''}`)
  },

  /** `id` is the task's `public_id` (ADR 0007) — the URL/route-param value. */
  get(id: string): Promise<Task> {
    return request(`/work/tasks/${id}`)
  },

  getActivity(id: number): Promise<{ items: ActivityItem[]; total: number }> {
    return request(`/work/tasks/${id}/activity`)
  },

  getSubscribers(id: number): Promise<{ items: TaskSubscriber[]; is_subscribed: boolean }> {
    return request(`/work/tasks/${id}/subscribers`)
  },

  subscribe(id: number): Promise<{ ok: boolean }> {
    return request(`/work/tasks/${id}/subscribe`, { method: 'POST' })
  },

  unsubscribe(id: number): Promise<void> {
    return request(`/work/tasks/${id}/subscribe`, { method: 'DELETE' })
  },

  create(data: { title: string; priority?: string; project_id?: number; phase_id?: number | null; due_date?: string; assigned_to?: number; description?: string; labels?: string[] }): Promise<Task> {
    return request('/work/tasks', { method: 'POST', body: JSON.stringify(data) })
  },

  update(id: string, data: Partial<Pick<Task, 'title' | 'description' | 'priority' | 'status' | 'assigned_to' | 'due_date' | 'labels' | 'project_id' | 'phase_id'>>): Promise<Task> {
    return request(`/work/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  },

  remove(id: string): Promise<{ ok: boolean }> {
    return request(`/work/tasks/${id}`, { method: 'DELETE' })
  },

  validate(id: number): Promise<Task> {
    return request(`/work/tasks/${id}/validate`, { method: 'POST' })
  },

  decline(id: number, reason?: string): Promise<Task> {
    return request(`/work/tasks/${id}/decline`, { method: 'POST', body: JSON.stringify({ reason }) })
  },

  approve(id: number): Promise<Task> {
    return request(`/work/tasks/${id}/approve`, { method: 'POST' })
  },

  reject(id: number, reason?: string): Promise<Task> {
    return request(`/work/tasks/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) })
  },

  flag(taskId: number, flag: 'at_risk' | 'blocked' | null, note?: string): Promise<Task> {
    return request<Task>(`/work/tasks/${taskId}/flag`, {
      method: 'PATCH',
      body: JSON.stringify({ flag, note }),
    })
  },

  logTime(taskId: number, minutes: number, note?: string): Promise<Task> {
    return request<Task>(`/work/tasks/${taskId}/time`, {
      method: 'POST',
      body: JSON.stringify({ minutes, note }),
    })
  },

  getDependencies(taskId: number): Promise<TaskDependencies> {
    return request(`/work/tasks/${taskId}/dependencies`)
  },

  addDependency(taskId: number, dependsOnId: number): Promise<{ id: number; task_id: number; depends_on_id: number }> {
    return request(`/work/tasks/${taskId}/dependencies`, {
      method: 'POST',
      body: JSON.stringify({ depends_on_id: dependsOnId }),
    })
  },

  removeDependency(taskId: number, depId: number): Promise<void> {
    return request(`/work/tasks/${taskId}/dependencies/${depId}`, { method: 'DELETE' })
  },

  snapshotTemplate(taskId: number, name: string): Promise<TaskTemplate> {
    return request(`/work/tasks/${taskId}/snapshot-template`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
  },

  getRecurrence(taskId: number): Promise<{ recurrence: RecurringTask | null }> {
    return request(`/work/tasks/${taskId}/recurrence`)
  },

  setRecurrence(taskId: number, recurrenceType: 'daily' | 'weekly' | 'monthly' | 'yearly' | null): Promise<Task> {
    return request(`/work/tasks/${taskId}/recurrence`, {
      method: 'PUT',
      body: JSON.stringify({ recurrence_type: recurrenceType }),
    })
  },
}
